// @flow
import React, { useState, useMemo } from 'react';
import * as DmsCommon from './../supports/dms-common';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import dayjs from "dayjs";
import Button from '@mui/material/Button';

import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-material.css';

import DocCheckHistEditDialog from './DocCheckHistEditDialog';
import MsgDialog from './MsgDialog';

function CardViewRenderer(params) {
  const d = params.data || {};
  const compactMode = params.context?.compactMode;

  const labelSx = {
    fontWeight: 600,
    minWidth: compactMode ? 88 : 105,
    flexShrink: 0,
    fontSize: compactMode ? 11 : 12,
    color: 'text.secondary',
  };

  const valueSx = {
    fontSize: compactMode ? 11 : 12,
    wordBreak: 'break-word',
  };

  const rowSx = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
    mb: 0.5,
  };

  const renderLine = (label, value) => {
    if (value === null || value === undefined) return null;

    const text = String(value).trim();
    if (!text) return null;

    return (
      <Box sx={rowSx}>
        <Typography component="span" sx={labelSx}>
          {label}
        </Typography>
        <Typography component="span" sx={valueSx}>
          {text}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        py: compactMode ? 0.5 : 1,
        px: compactMode ? 0.5 : 1,
        lineHeight: 1.35,
      }}
    >
      {renderLine("Request By", d.RequestBy)}
      {renderLine("Request Date", d.RequestDateDisplay)}
      {renderLine("Recipient", d.Solicitor)}
      {renderLine("Handled By", d.HandleBy)}
      {renderLine("Handled Date", d.HandleDateDisplay)}
      {renderLine("Send Out By", d.SentOutBy)}
      {renderLine("Send Out Date", d.SentOutDateDisplay)}
      {renderLine("Check-In By", d.CheckInBy)}
      {renderLine("Return By", d.ReturnBy)}
      {renderLine("Return Date", d.ReturnDateDisplay)}
      {renderLine("File Location", d.FileLocation)}
      {renderLine("Created By", d.CreatedByName)}
      {renderLine("Created Date", d.CreatedDateDisplay)}
      {renderLine("Updated By", d.UpdatedByName)}
      {renderLine("Updated Date", d.UpdatedDateDisplay)}
    </Box>
  );
}

export default function DocCheckHistTab(props) {
  const {
    sysID,
    docDetail,
    docFolder,
    afterUpdate,
    compactMode,
    cardViewMode,
    isFolderOffline,
  } = props;

  const checkOutHistData = docDetail.CheckOutHists || [];

  const [openHandleAddEditDialog, setOpenHandleAddEditDialog] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [isCheckIn, setIsCheckIn] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [viewCheckLog, setViewCheckLog] = useState(null);
  const [selectedRecs, setSelectedRecs] = useState([]);

  const useCardView = cardViewMode;

  const onSelectionChanged = (params) => {
    const nodes = params.api.getSelectedNodes();

    const selRecs = nodes.map((item) => item.data);

    setSelectedRecs(selRecs);
    if (selRecs.length === 1) {
      setViewCheckLog(selRecs[0]);
    }
  };

  const handleViewClick = () => {
    setViewMode(true);
    setIsCheckIn(false);
    setOpenHandleAddEditDialog(true);
  };

  const handleCheckInClick = () => {
    if (isFolderOffline) {
      setErrMsg("The path is offline");
      setShowError(true);
      return;
    }
    setViewMode(false);
    setIsCheckIn(true);
    setOpenHandleAddEditDialog(true);
  };

  const handleHandleClick = () => {
    if (isFolderOffline) {
      setErrMsg("The path is offline");
      setShowError(true);
      return;
    }
    setViewMode(false);
    setIsCheckIn(false);
    setOpenHandleAddEditDialog(true);
  };

  const handleAfterUpdate = () => {
    setOpenHandleAddEditDialog(false);
    afterUpdate && afterUpdate();
  };

  const gridData = checkOutHistData.map((item) => {
    return {
      ...item,
      CreatedDateJs: dayjs(item.CreatedDate),
      CreatedDateDisplay: dayjs(item.CreatedDate).format(DmsCommon.SHORT_DATE_FORMAT),
      HandleDateJs: dayjs(item.HandleDate),
      HandleDateDisplay: item.HandleDate ? dayjs(item.HandleDate).format(DmsCommon.SHORT_DATE_FORMAT) : "",
      RequestDateJs: dayjs(item.RequestDate),
      RequestDateDisplay: item.RequestDate ? dayjs(item.RequestDate).format(DmsCommon.SHORT_DATE_FORMAT) : "",
      ReturnDateJs: dayjs(item.ReturnDate),
      ReturnDateDisplay: item.ReturnDate ? dayjs(item.ReturnDate).format(DmsCommon.SHORT_DATE_FORMAT) : "",
      SentOutDateJs: dayjs(item.SentOutDate),
      SentOutDateDisplay: item.SentOutDate ? dayjs(item.SentOutDate).format(DmsCommon.SHORT_DATE_FORMAT) : "",
      UpdatedDateJs: dayjs(item.UpdatedDate),
      UpdatedDateDisplay: item.UpdatedDate ? dayjs(item.UpdatedDate).format(DmsCommon.SHORT_DATE_FORMAT) : "",
    };
  });

  const cellStyle = useMemo(() => ({
    paddingLeft: compactMode ? '6px' : '10px',
    paddingRight: compactMode ? '6px' : '10px',
    fontSize: compactMode ? '0.7rem' : '0.8rem',
  }), [compactMode]);

  const compareDateField = (fieldName) => (a, b, nodeA, nodeB) => {
    const aDate = nodeA.data[fieldName];
    const bDate = nodeB.data[fieldName];

    if (!aDate && !bDate) return 0;
    if (!aDate) return -1;
    if (!bDate) return 1;

    if (aDate.isBefore(bDate)) return -1;
    if (aDate.isAfter(bDate)) return 1;
    return 0;
  };

  const compareStrInsensitive = (aStrA, aStrB) => {
    return (aStrA || '').localeCompare((aStrB || ''), undefined, {
      sensitivity: "base"
    });
  };

  const compareStringField = (fieldName) => (a, b, nodeA, nodeB) => {
    return compareStrInsensitive(nodeA.data[fieldName], nodeB.data[fieldName]);
  };

  const columnDefs = useMemo(() => [
    { field: 'RequestBy', headerName: "Request By", cellStyle, width: 150, comparator: compareStringField('RequestBy') },
    { field: 'RequestDateDisplay', headerName: "Request Date", cellStyle, comparator: compareDateField('RequestDateJs') },
    { field: 'Solicitor', headerName: "Recipient", cellStyle, comparator: compareStringField('Solicitor') },
    { field: 'HandleBy', headerName: "Handled By", cellStyle, width: 150, comparator: compareStringField('HandleBy') },
    { field: 'HandleDateDisplay', headerName: "Handled Date", cellStyle, comparator: compareDateField('HandleDateJs') },
    { field: 'SentOutBy', headerName: "Send Out By", cellStyle, width: 150, comparator: compareStringField('SentOutBy') },
    { field: 'SentOutDateDisplay', headerName: "Send Out Date", cellStyle, comparator: compareDateField('SentOutDateJs') },
    { field: 'CheckInBy', headerName: "Check-In By", cellStyle, comparator: compareStringField('CheckInBy') },
    { field: 'ReturnBy', headerName: "Return By", cellStyle, width: 150, comparator: compareStringField('ReturnBy') },
    { field: 'ReturnDateDisplay', headerName: "Return Date", cellStyle, comparator: compareDateField('ReturnDateJs') },
    { field: 'FileLocation', headerName: "File Location", cellStyle, comparator: compareStringField('FileLocation') },
    { field: 'CreatedByName', headerName: "Created By", cellStyle, width: 150, comparator: compareStringField('CreatedByName') },
    { field: 'CreatedDateDisplay', headerName: "Created Date", cellStyle, comparator: compareDateField('CreatedDateJs') },
    { field: 'UpdatedByName', headerName: "Updated By", cellStyle, width: 150, comparator: compareStringField('UpdatedByName') },
    { field: 'UpdatedDateDisplay', headerName: "Updated Date", cellStyle, comparator: compareDateField('UpdatedDateJs') },
  ], [cellStyle]);

  const columnDefsCard = useMemo(() => [
    {
      headerName: "History",
      colId: "cardView",
      flex: 1,
      sortable: false,
      resizable: true,
      suppressMovable: true,
      autoHeight: true,
      cellRenderer: CardViewRenderer,
      cellStyle: {
        padding: compactMode ? '4px' : '8px',
        whiteSpace: 'normal',
        lineHeight: 1.35,
      },
    },
  ], [compactMode]);

  const allowHandle = docFolder
    ? (docFolder.Grade >= DmsCommon.AccessRightEnum.arReadHandle)
    : false;

  const histCnt = checkOutHistData.length;
  const lastCheckOutHist = histCnt > 0 ? checkOutHistData[histCnt - 1] : null;

  const allowCheckIn = docFolder
    ? (docFolder.Grade >= DmsCommon.AccessRightEnum.arReadHandle) &&
    (lastCheckOutHist && lastCheckOutHist.Handled && !lastCheckOutHist.Completed)
    : false;

  const rowHeight = compactMode ? 28 : 36;
  const headerHeight = compactMode ? 30 : 40;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: "70vh",
        width: 1,
        maxWidth: 1,
        minWidth: 0,
        overflow: 'hidden',
        px: 0,
      }}
    >
      <MsgDialog
        open={showError}
        title="Error"
        msgText={errMsg}
        onClose={() => setShowError(false)}
      />
      <DocCheckHistEditDialog
        sysID={sysID}
        docDetail={docDetail}
        open={openHandleAddEditDialog}
        onClose={() => setOpenHandleAddEditDialog(false)}
        afterUpdate={handleAfterUpdate}
        viewMode={viewMode}
        viewCheckLog={viewCheckLog}
        isCheckIn={isCheckIn}
      />

      <Box sx={{ py: 1, px: 1 }}>
        <Button
          disabled={selectedRecs.length !== 1}
          onClick={handleViewClick}
          sx={{ fontSize: compactMode ? 11 : undefined }}
        >
          View
        </Button>
        <Button
          disabled={!allowHandle}
          onClick={handleHandleClick}
          sx={{ fontSize: compactMode ? 11 : undefined }}
        >
          Handle
        </Button>
        <Button
          disabled={!allowCheckIn}
          onClick={handleCheckInClick}
          sx={{ fontSize: compactMode ? 11 : undefined }}
        >
          Check In
        </Button>
      </Box>

      <Box
        className="ag-theme-material"
        sx={{
          flex: 1,
          width: 1,
          maxWidth: 1,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <AgGridReact
          rowData={gridData}
          context={{ compactMode }}
          defaultColDef={{
            width: 100,
            resizable: true,
            sortable: true,
            suppressMovable: true,
          }}
          columnDefs={useCardView ? columnDefsCard : columnDefs}
          rowHeight={useCardView ? undefined : rowHeight}
          headerHeight={headerHeight}
          suppressNoRowsOverlay
          rowSelection="single"
          onSelectionChanged={onSelectionChanged}

          getRowId={(params) => params.data.id}
        />
      </Box>
    </Box>
  );
}

DocCheckHistTab.propTypes = {
  sysID: PropTypes.string,
  docDetail: PropTypes.object,
  docFolder: PropTypes.object,
  afterUpdate: PropTypes.func,
  compactMode: PropTypes.bool,
  cardViewMode: PropTypes.bool,
  isFolderOffline: PropTypes.bool,
};

DocCheckHistTab.defaultProps = {
  compactMode: false,
  cardViewMode: false,
};